import { PropertyDeniedDialog } from '@/features/quotes/components/quote-steps/property-denied.dialog';
import type { Proposal } from '@/services/proposals/types';
import { FieldComponents } from '@/shared/components/form-fields/types';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { useFetchZipcode } from '@/shared/hooks/use-fetch-zipcode.hook';
import { isRegisterNumberCnpjMask } from '@/shared/utils/format-register-number';
import {
  validateEmail,
  validateEndDate,
  validatePhone,
  validateRegisterNumberDocument,
  normalizeEmail,
} from '@/shared/utils';
import {
  Button,
  Spinner,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { toaster } from '@/shared/ui/toaster.component';
import { formOptions } from '@tanstack/react-form';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { withForm } from '../../hooks/use-proposal-form.hook';
import { useUpdateProposal } from '../../hooks/use-update-proposal.hook';
import {
  InspectionContactSection,
  type InspectionContactSectionHandle,
} from './inspection-contact-section.component';

/**
 * 422 do attach-element com `proposalStatus.code === '2'` indica que a
 * cotação foi encerrada (ex.: situação cadastral irregular na Receita).
 */
function isQuoteClosedError(error: unknown): error is ValidationError {
  return (
    error instanceof ValidationError &&
    error.fieldErrors?.proposalStatus?.code === String(PortalStatus.QuoteClosed)
  );
}

const formOpts = formOptions({
  defaultValues: {
    pk: 0,
    name: '',
    hasSocialName: false,
    socialName: '',
    registerNumber: '',
    gender: '',
    email: '',
    startDate: '',
    endDate: '',
    birthDate: '',
    phone: '',
    billingCep: '',
    billingStreet: '',
    billingNumber: '',
    billingComplement: '',
    billingNeighborhood: '',
    billingCity: '',
    billingState: '',
    useInsuredAddressAsBillingAddress: false,
    originalAddress: {
      cep: '',
      street: '',
      addressNumber: '',
      addressComplement: '',
      neighborhood: '',
      city: '',
      state: '',
    },
    selectedRiskPk: '',
  },
});

export const StepInsureData = withForm({
  ...formOpts,
  props: {
    onNext: (_id?: string) => {},
    onBack: () => {},
    isViewMode: false,
    proposalId: '',
    proposal: undefined as Proposal | undefined,
    isBusinessProposal: false,
  },
  render: ({
    form,
    onNext,
    onBack,
    isViewMode,
    proposalId,
    proposal,
    isBusinessProposal,
  }: {
    form: any;
    onNext: () => void;
    onBack: () => void;
    isViewMode: boolean;
    proposalId: string;
    proposal: Proposal | undefined;
    isBusinessProposal: boolean;
  }) => {
    const {
      mutateAsync: updateProposal,
      isPending: isUpdatingProposal,
      error: updateProposalError,
    } = useUpdateProposal(proposalId, proposal?.productCode);
    const quoteClosedError = isQuoteClosedError(updateProposalError)
      ? updateProposalError
      : null;
    const { mutateAsync: fetchZipcode, isPending: isLoadingCep } =
      useFetchZipcode('billing');
    const inspectionContactRef = useRef<InspectionContactSectionHandle>(null);
    const zipcodeLookupCacheRef = useRef<Record<string, boolean>>({});
    const [hasResolvedCep, setHasResolvedCep] = useState(false);
    const [isGenericCep, setIsGenericCep] = useState(false);
    const [billingCepKey, setBillingCepKey] = useState(0);
    const canEditStreetAndNeighborhood = hasResolvedCep && isGenericCep;
    const isLegalPerson = useMemo(() => {
      const registerNumber = String(
        form.state.values.registerNumber || ''
      ).trim();

      return (
        proposal?.customer?.documentType === '2' ||
        proposal?.customer?.isOrgParty === 'Y' ||
        isRegisterNumberCnpjMask(registerNumber)
      );
    }, [
      form.state.values.registerNumber,
      proposal?.customer?.documentType,
      proposal?.customer?.isOrgParty,
    ]);

    const hasResolvedAddress = useMemo(() => {
      return (
        form.state.values.billingCep !== '' &&
        form.state.values.billingStreet !== '' &&
        form.state.values.billingNeighborhood !== '' &&
        form.state.values.billingCity !== '' &&
        form.state.values.billingState !== ''
      );
    }, [
      form.state.values.billingCep,
      form.state.values.billingStreet,
      form.state.values.billingNeighborhood,
      form.state.values.billingCity,
      form.state.values.billingState,
    ]);

    const useInsuredAddressAsBillingAddress =
      !!form.state.values.useInsuredAddressAsBillingAddress;

    useEffect(() => {
      const cleanCep = String(form.state.values.billingCep || '').replace(
        /\D/g,
        ''
      );
      if (cleanCep.length !== 8) {
        if (hasResolvedCep) setHasResolvedCep(false);
        if (isGenericCep) setIsGenericCep(false);
        return;
      }
      if (!hasResolvedCep) setHasResolvedCep(true);
      const nextIsGeneric = cleanCep.endsWith('000');
      if (nextIsGeneric !== isGenericCep) setIsGenericCep(nextIsGeneric);
    }, [form.state.values.billingCep, hasResolvedCep, isGenericCep]);

    useEffect(() => {
      if (!isLegalPerson) return;
      if (!form.state.values.gender && !form.state.values.birthDate) return;

      form.setFieldValue('gender', '');
      form.setFieldValue('birthDate', '');
    }, [
      form,
      form.state.values.birthDate,
      form.state.values.gender,
      isLegalPerson,
    ]);

    useEffect(() => {
      if (!isLegalPerson) return;
      if (!form.state.values.hasSocialName && !form.state.values.socialName) {
        return;
      }

      form.setFieldValue('hasSocialName', false);
      form.setFieldValue('socialName', '');
    }, [
      form,
      form.state.values.hasSocialName,
      form.state.values.socialName,
      isLegalPerson,
    ]);

    const propertyRisks = useMemo(() => {
      if (!isBusinessProposal) return [];
      const risks = proposal?.lobs?.flatMap(lob => lob.risks) ?? [];
      return risks.filter(risk => risk?.pk);
    }, [isBusinessProposal, proposal?.lobs]);

    const selectedRiskPk = String(form.state.values.selectedRiskPk || '');

    const syncSelectedRiskAddress = (
      riskPk: string,
      syncBilling: boolean
    ) => {
      const risk = propertyRisks.find(item => String(item?.pk) === riskPk);
      if (!risk) return;

      form.setFieldValue('originalAddress', {
        cep: risk.zipCode || '',
        street: risk.address || '',
        addressNumber: risk.addressNumber || '',
        addressComplement: risk.addressComplement || '',
        neighborhood: risk.neighborhood || '',
        city: risk.city || '',
        state: risk.state || '',
      });

      if (!syncBilling) return;

      form.setFieldValue('billingCep', risk.zipCode || '');
      form.setFieldValue('billingStreet', risk.address || '');
      form.setFieldValue('billingNumber', risk.addressNumber || '');
      form.setFieldValue('billingComplement', risk.addressComplement || '');
      form.setFieldValue('billingNeighborhood', risk.neighborhood || '');
      form.setFieldValue('billingCity', risk.city || '');
      form.setFieldValue('billingState', risk.state || '');

      const cleanCep = (risk.zipCode || '').replace(/\D/g, '');
      if (cleanCep.length === 8) {
        setHasResolvedCep(true);
        setIsGenericCep(cleanCep.endsWith('000'));
      }
      setBillingCepKey(k => k + 1);
    };

    const fillBillingFromInsuredAddress = () => {
      if (isBusinessProposal && selectedRiskPk) {
        syncSelectedRiskAddress(selectedRiskPk, true);
        return;
      }

      const original = form.state.values.originalAddress;
      const cep = original.cep || '';
      form.setFieldValue('billingCep', cep);
      form.setFieldValue('billingStreet', original.street || '');
      form.setFieldValue('billingNumber', original.addressNumber || '');
      form.setFieldValue('billingComplement', original.addressComplement || '');
      form.setFieldValue('billingNeighborhood', original.neighborhood || '');
      form.setFieldValue('billingCity', original.city || '');
      form.setFieldValue('billingState', original.state || '');

      const cleanCep = cep.replace(/\D/g, '');
      if (cleanCep.length === 8) {
        setHasResolvedCep(true);
        setIsGenericCep(cleanCep.endsWith('000'));
      }
    };

    useEffect(() => {
      if (!isBusinessProposal || !selectedRiskPk) return;

      syncSelectedRiskAddress(
        selectedRiskPk,
        useInsuredAddressAsBillingAddress
      );
    }, [
      isBusinessProposal,
      selectedRiskPk,
      useInsuredAddressAsBillingAddress,
      propertyRisks,
    ]);

    const clearBillingAddress = () => {
      form.setFieldValue('billingCep', '');
      form.setFieldValue('billingStreet', '');
      form.setFieldValue('billingNumber', '');
      form.setFieldValue('billingComplement', '');
      form.setFieldValue('billingNeighborhood', '');
      form.setFieldValue('billingCity', '');
      form.setFieldValue('billingState', '');
      setHasResolvedCep(false);
      setIsGenericCep(false);
      zipcodeLookupCacheRef.current = {};
      setBillingCepKey(k => k + 1);
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = today.toISOString().split('T')[0];

    const minAgeDate =
      DateTime.fromISO(minDate).minus({ years: 99 }).toISODate() || '';

    const fetchAndFillZipcode = async (value: string) => {
      const cleanCep = value.replace(/\D/g, '');
      if (cleanCep.length !== 8) return;

      try {
        const result = await fetchZipcode(cleanCep);

        if (result.success && result.data && !('error' in result.data)) {
          const addressData = result.data;
          zipcodeLookupCacheRef.current[cleanCep] = true;
          const isGeneric = cleanCep.endsWith('000');
          setHasResolvedCep(true);
          setIsGenericCep(isGeneric);

          form.setFieldValue('billingStreet', addressData.street || '');
          form.setFieldValue(
            'billingNeighborhood',
            addressData.neighborhood || ''
          );
          form.setFieldValue('billingCity', addressData.city || '');
          form.setFieldValue('billingState', addressData.state || '');
          return;
        }

        zipcodeLookupCacheRef.current[cleanCep] = false;
        setHasResolvedCep(false);
        setIsGenericCep(false);
        form.setFieldValue('billingStreet', '');
        form.setFieldValue('billingNeighborhood', '');
        form.setFieldValue('billingCity', '');
        form.setFieldValue('billingState', '');
        toaster.create({
          type: 'error',
          title: 'CEP não encontrado',
          description: 'Verifique o CEP informado e tente novamente.',
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error && error.message.trim()
            ? error.message
            : 'Verifique o CEP informado e tente novamente.';
        zipcodeLookupCacheRef.current[cleanCep] = false;
        setHasResolvedCep(false);
        setIsGenericCep(false);
        form.setFieldValue('billingStreet', '');
        form.setFieldValue('billingNeighborhood', '');
        form.setFieldValue('billingCity', '');
        form.setFieldValue('billingState', '');
        toaster.create({
          type: 'error',
          title: 'Erro ao buscar CEP',
          description: errorMessage,
        });
      }
    };

    const handleSubmit = async () => {
      if (isViewMode) {
        onNext?.();
        return;
      }

      const validationResult = await form.validateAllFields('change');
      const hasFormErrors = validationResult.length > 0;

      const hasInspectionErrors =
        proposal?.requiresInspection === 'S' &&
        inspectionContactRef.current &&
        !inspectionContactRef.current.validate();

      if (hasFormErrors || hasInspectionErrors) {
        return;
      }

      try {
        const customer = {
          pk: form.state.values.pk,
          socialName:
            !isLegalPerson && form.state.values.hasSocialName
              ? form.state.values.socialName
              : '',
          cellphone: form.state.values.phone,
          email: normalizeEmail(form.state.values.email),
          ...(isLegalPerson
            ? {}
            : {
                genderCode: form.state.values.gender as 'M' | 'F',
                dateOfBirth: form.state.values.birthDate,
              }),
        };

        await updateProposal({
          effectiveDate: form.state.values.startDate,
          terminateDate: form.state.values.endDate,
          customer,
          billingAddress: {
            neighborhood: form.state.values.billingNeighborhood,
            cep: form.state.values.billingCep,
            city: form.state.values.billingCity,
            state: form.state.values.billingState,
            street: form.state.values.billingStreet,
            addressComplement: form.state.values.billingComplement,
            addressNumber: form.state.values.billingNumber,
            countryCode: 'BR',
          },
          ...(proposal?.requiresInspection === 'S' &&
          inspectionContactRef.current
            ? {
                inspectionContacts: inspectionContactRef.current
                  .getContacts()
                  .map(c => ({
                    name: c.name,
                    email: normalizeEmail(c.email),
                    phone: c.phone.replace(/\D/g, ''),
                  })),
              }
            : {}),
        });

        onNext?.();
      } catch (error) {
        console.error('Error updating proposal:', error);

        // Cotação encerrada abre o dialog de encerramento, renderizado a
        // partir do estado de erro da mutation.
        if (!isQuoteClosedError(error)) {
          const description =
            error instanceof Error && error.message.trim()
              ? error.message
              : 'Tente novamente.';
          toaster.create({
            type: 'error',
            title: 'Erro ao atualizar proposta',
            description,
          });
        }
      }
    };

    return (
      <div className='space-y-6'>
        <div className='flex flex-col gap-4'>
          <Text className='text-lg font-bold text-alba-black-500'>
            Dados da Emissão
          </Text>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-10'>
            <form.AppField
              name='startDate'
              validators={{
                onChange: ({ value }: { value: string | undefined }) => {
                  if (isViewMode) return undefined;
                  if (!value) return 'Início da vigência é obrigatório';
                  if (DateTime.fromISO(value).diffNow().as('days') <= -1)
                    return 'A data de início não pode ser anterior à data atual';
                  return undefined;
                },
              }}
              children={(field: FieldComponents) => (
                <field.CalendarField
                  label='Início da Vigência'
                  placeholder='Selecione a data'
                  required
                  minDate={minDate}
                  disabled={isViewMode}
                />
              )}
            />

            <form.AppField
              name='endDate'
              validators={{
                onBlur: isViewMode
                  ? undefined
                  : validateEndDate(form.state.values.startDate),
                onChange: isViewMode
                  ? undefined
                  : validateEndDate(form.state.values.startDate),
              }}
              children={(field: FieldComponents) => (
                <field.CalendarField
                  label='Fim da Vigência'
                  placeholder='Selecione a data'
                  minDate={minDate}
                  disabled
                />
              )}
            />
          </div>

          <form.Subscribe
            selector={(state: {
              values: { startDate: string; endDate: string };
            }) => ({
              startDate: state.values.startDate,
              endDate: state.values.endDate,
            })}
            children={({
              startDate,
              endDate,
            }: {
              startDate: string;
              endDate: string;
            }) => {
              if (!startDate) {
                if (endDate) {
                  setTimeout(() => {
                    form.setFieldValue('endDate', '');
                  }, 0);
                }
                return null;
              }

              const start = new Date(startDate);

              if (Number.isNaN(start.getTime())) {
                return null;
              }

              const end = new Date(start);
              end.setFullYear(end.getFullYear() + 1);

              const endDateFormatted = end.toISOString().split('T')[0];

              if (endDate !== endDateFormatted) {
                setTimeout(() => {
                  form.setFieldValue('endDate', endDateFormatted);
                }, 0);
              }

              return null;
            }}
          />
        </div>

        <div className='flex flex-col gap-4'>
          <Text className='text-lg font-bold text-alba-black-500'>
            Dados do Proponente
          </Text>

          <div className='space-y-4'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
              <form.AppField
                name='registerNumber'
                validators={{
                  onChange: ({ value }: { value: string | undefined }) => {
                    if (isViewMode) return undefined;
                    return validateRegisterNumberDocument(value);
                  },
                }}
                children={(field: FieldComponents) => (
                  <form.Subscribe
                    selector={(state: { values: { registerNumber: string } }) =>
                      state.values.registerNumber
                    }
                    children={(registerNumber: string) => {
                      // Empresarial também aceita pessoa física: a máscara e o
                      // label seguem o documento carregado, não o produto.
                      const useCnpj = registerNumber
                        ? isRegisterNumberCnpjMask(registerNumber)
                        : isLegalPerson;
                      return (
                        <field.TextField
                          key={useCnpj ? 'cnpj' : 'cpf'}
                          label={
                            isBusinessProposal
                              ? useCnpj
                                ? 'CNPJ'
                                : 'CPF'
                              : 'CPF/CNPJ'
                          }
                          placeholder={
                            useCnpj ? 'XX.XXX.XXX/XXXX-XX' : 'XXX.XXX.XXX-XX'
                          }
                          required
                          maxLength={
                            useCnpj
                              ? FIELD_MAX_LENGTHS.cnpj
                              : FIELD_MAX_LENGTHS.cpf
                          }
                          mask={useCnpj ? 'cnpj' : 'cpf'}
                          disabled
                        />
                      );
                    }}
                  />
                )}
              />

              <form.AppField
                name='name'
                validators={{
                  onChange: ({ value }: { value: string | undefined }) => {
                    if (isViewMode) return undefined;
                    if (!value) return 'Nome é obrigatório';
                    return undefined;
                  },
                }}
                children={(field: FieldComponents) => (
                  <field.TextField
                    label={isLegalPerson ? 'Razão Social' : 'Nome'}
                    placeholder={
                      isLegalPerson
                        ? 'Razão social da empresa'
                        : 'Digite o nome do proponente'
                    }
                    required
                    maxLength={FIELD_MAX_LENGTHS.personName}
                    disabled
                  />
                )}
              />
            </div>

            {!isLegalPerson && (
              <>
                <form.AppField
                  name='hasSocialName'
                  children={(field: FieldComponents) => (
                    <field.CheckboxField
                      label='Possui Nome Social?'
                      disabled={isViewMode}
                    />
                  )}
                />

                <form.Subscribe
                  selector={(state: {
                    values: { hasSocialName: boolean };
                  }) => ({
                    hasSocialName: state.values.hasSocialName,
                  })}
                  children={({
                    hasSocialName,
                  }: {
                    hasSocialName: boolean;
                  }) =>
                    hasSocialName ? (
                      <form.AppField
                        name='socialName'
                        validators={{
                          onChange: ({
                            value,
                          }: {
                            value: string | undefined;
                          }) => {
                            if (isViewMode) return undefined;
                            if (!value) return 'O nome social é obrigatório';
                            return undefined;
                          },
                        }}
                        children={(field: FieldComponents) => (
                          <field.TextField
                            label='Nome Social'
                            placeholder='Digite o nome social do proponente'
                            required
                            maxLength={FIELD_MAX_LENGTHS.socialName}
                            disabled={isViewMode}
                          />
                        )}
                      />
                    ) : null
                  }
                />
              </>
            )}

            {!isLegalPerson ? (
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                <form.AppField
                  name='gender'
                  validators={{
                    onChange: ({ value }: { value: string | undefined }) => {
                      if (isViewMode || isLegalPerson) return undefined;
                      if (!value) return 'Sexo é obrigatório';
                      if (value !== 'M' && value !== 'F')
                        return 'Sexo inválido';
                      return undefined;
                    },
                  }}
                  children={(field: FieldComponents) => (
                    <field.SelectField
                      label='Sexo'
                      placeholder='Selecione o sexo'
                      required
                      disabled={isViewMode}
                      options={[
                        { value: 'M', label: 'Masculino' },
                        { value: 'F', label: 'Feminino' },
                      ]}
                    />
                  )}
                />

                <form.AppField
                  name='birthDate'
                  validators={{
                    onChange: ({ value }: { value: string | undefined }) => {
                      if (isViewMode || isLegalPerson) return undefined;
                      if (!value) return 'Data de nascimento é obrigatória';

                      const parsed = DateTime.fromISO(value);
                      if (!parsed.isValid) return 'Data de nascimento inválida';

                      const ageInYears = DateTime.now().diff(
                        parsed,
                        'years'
                      ).years;

                      if (ageInYears < 18 || ageInYears > 99)
                        return 'A data de nascimento não é válida!';

                      return undefined;
                    },
                  }}
                  children={(field: FieldComponents) => (
                    <field.DateField
                      label='Data de Nascimento'
                      placeholder='dd/mm/aaaa'
                      required
                      minDate={minAgeDate}
                      maxDate={minDate}
                      disabled={isViewMode}
                    />
                  )}
                />
              </div>
            ) : null}

            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
              <form.AppField
                name='phone'
                validators={{
                  onChange: ({ value }: { value: string | undefined }) => {
                    if (isViewMode) return undefined;
                    if (!value) return 'Telefone é obrigatório';
                    if (!validatePhone(value)) return 'Telefone inválido';
                    return undefined;
                  },
                }}
                children={(field: FieldComponents) => (
                  <field.TextField
                    label='Telefone'
                    placeholder='(XX) XXXX-XXXX'
                    required
                    maxLength={FIELD_MAX_LENGTHS.phone}
                    mask='(99) 99999-9999'
                    disabled={isViewMode}
                  />
                )}
              />

              <form.AppField
                name='email'
                validators={{
                  onChange: ({ value }: { value: string | undefined }) => {
                    if (isViewMode) return undefined;
                    if (!value) return 'E-mail é obrigatório';
                    if (!validateEmail(value)) return 'E-mail inválido';
                    return undefined;
                  },
                }}
                children={(field: FieldComponents) => (
                  <field.TextField
                    label='E-mail'
                    lowercase
                    placeholder='exemplo@email.com'
                    required
                    maxLength={FIELD_MAX_LENGTHS.email}
                    disabled={isViewMode}
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className='space-y-4'>
          <Text className='text-lg font-bold text-alba-black-500'>
            Endereço de Cobrança
          </Text>

          <form.AppField
            name='useInsuredAddressAsBillingAddress'
            children={(field: FieldComponents) => (
              <field.CheckboxField
                label='Mesmo endereço do Imóvel segurado?'
                disabled={isViewMode}
                onChange={newValue => {
                  if (newValue) {
                    fillBillingFromInsuredAddress();
                  } else {
                    clearBillingAddress();
                  }
                }}
              />
            )}
          />

          {isBusinessProposal && propertyRisks.length > 0 && (
            <form.AppField
              name='selectedRiskPk'
              validators={{
                onChange: ({ value }: { value: string | undefined }) => {
                  if (isViewMode) return undefined;
                  if (!value) return 'Selecione um imóvel';
                  return undefined;
                },
              }}
              children={(field: FieldComponents) => (
                <field.SelectField
                  label='Lista de Imóveis'
                  placeholder='Selecione um imóvel'
                  required
                  disabled={isViewMode}
                  options={propertyRisks.map((risk, index) => ({
                    value: String(risk.pk),
                    label: `Local ${String(index + 1).padStart(2, '0')} - ${[
                      risk.address,
                      risk.addressNumber,
                    ]
                      .filter(Boolean)
                      .join(', ')}${risk.city ? `, ${risk.city}` : ''}${
                      risk.state ? ` - ${risk.state}` : ''
                    }`,
                  }))}
                  onValueChange={nextRiskPk => {
                    if (useInsuredAddressAsBillingAddress) {
                      syncSelectedRiskAddress(nextRiskPk, true);
                    }
                  }}
                />
              )}
            />
          )}

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
            <form.AppField
              key={billingCepKey}
              name='billingCep'
              validators={{
                onChange: ({ value }: { value: string | undefined }) => {
                  if (isViewMode) return undefined;
                  const stringValue = String(value || '');
                  if (!stringValue || stringValue.trim() === '') {
                    return 'CEP é obrigatório';
                  }
                  const cleanCep = stringValue.replace(/\D/g, '');
                  if (cleanCep.length !== 8) {
                    return 'CEP deve ter 8 dígitos';
                  }
                  if (zipcodeLookupCacheRef.current[cleanCep] === false) {
                    return 'CEP inválido ou não encontrado';
                  }
                  return undefined;
                },
              }}
              children={(field: FieldComponents) => (
                <field.TextField
                  label='CEP'
                  placeholder='XXXXX-XXX'
                  required
                  maxLength={FIELD_MAX_LENGTHS.cep}
                  mask='99999-999'
                  disabled={
                    isLoadingCep ||
                    isViewMode ||
                    useInsuredAddressAsBillingAddress
                  }
                  onBlur={async () => {
                    const value = form.state.values.billingCep;
                    const cleanCep = String(value || '').replace(/\D/g, '');
                    if (cleanCep.length !== 8) return;
                    await fetchAndFillZipcode(String(value || ''));
                    await form.validateField(
                      'billingCep' as keyof typeof form.state.values,
                      'change'
                    );
                  }}
                />
              )}
            />
          </div>

          {/* Mostrar os campos apenas após a resolução do CEP (ou em modo visualização). */}
          {(() => {
            const shouldShowAddressFields =
              hasResolvedCep || isViewMode || hasResolvedAddress;

            if (!shouldShowAddressFields) {
              return null;
            }

            return (
              <form.Subscribe
                selector={(state: {
                  values: {
                    billingCep: string;
                  };
                }) => ({
                  showAddressFields: state.values.billingCep !== '',
                })}
                children={({
                  showAddressFields,
                }: {
                  showAddressFields: boolean;
                }) => {
                  if (!showAddressFields) {
                    return null;
                  }

                  return (
                    <>
                      <form.AppField
                        name='billingStreet'
                        children={(field: FieldComponents) => (
                          <field.TextField
                            label='Endereço'
                            placeholder='Rua, Avenida ...'
                            required
                            maxLength={FIELD_MAX_LENGTHS.street}
                            disabled={
                              isViewMode ||
                              useInsuredAddressAsBillingAddress ||
                              !canEditStreetAndNeighborhood
                            }
                          />
                        )}
                      />

                      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                        <form.AppField
                          name='billingNumber'
                          validators={{
                            onChange: ({
                              value,
                            }: {
                              value: string | undefined;
                            }) => {
                              if (isViewMode) return undefined;
                              const stringValue = String(value || '');
                              if (!stringValue || stringValue.trim() === '') {
                                return 'Número é obrigatório';
                              }
                              return undefined;
                            },
                          }}
                          children={(field: FieldComponents) => (
                            <field.TextField
                              label='Número'
                              placeholder='Número'
                              required
                              maxLength={FIELD_MAX_LENGTHS.addressNumber}
                              disabled={
                                isViewMode || useInsuredAddressAsBillingAddress
                              }
                            />
                          )}
                        />

                        <form.AppField
                          name='billingComplement'
                          children={(field: FieldComponents) => (
                            <field.TextField
                              label='Complemento'
                              placeholder='Apto, Bloco ...'
                              maxLength={FIELD_MAX_LENGTHS.complement}
                              disabled={
                                isViewMode || useInsuredAddressAsBillingAddress
                              }
                            />
                          )}
                        />
                      </div>

                      <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
                        <form.AppField
                          name='billingNeighborhood'
                          children={(field: FieldComponents) => (
                            <field.TextField
                              label='Bairro'
                              placeholder='Bairro'
                              required
                              maxLength={FIELD_MAX_LENGTHS.neighborhood}
                              disabled={
                                isViewMode ||
                                useInsuredAddressAsBillingAddress ||
                                !canEditStreetAndNeighborhood
                              }
                            />
                          )}
                        />

                        <form.AppField
                          name='billingCity'
                          children={(field: FieldComponents) => (
                            <field.TextField
                              label='Cidade'
                              placeholder='Cidade'
                              required
                              maxLength={FIELD_MAX_LENGTHS.city}
                              disabled={true}
                            />
                          )}
                        />

                        <form.AppField
                          name='billingState'
                          children={(field: FieldComponents) => (
                            <field.SelectField
                              label='Estado'
                              placeholder='UF'
                              required
                              options={[
                                { value: 'AC', label: 'Acre' },
                                { value: 'AL', label: 'Alagoas' },
                                { value: 'AP', label: 'Amapá' },
                                { value: 'AM', label: 'Amazonas' },
                                { value: 'BA', label: 'Bahia' },
                                { value: 'CE', label: 'Ceará' },
                                { value: 'DF', label: 'Distrito Federal' },
                                { value: 'ES', label: 'Espírito Santo' },
                                { value: 'GO', label: 'Goiás' },
                                { value: 'MA', label: 'Maranhão' },
                                { value: 'MT', label: 'Mato Grosso' },
                                { value: 'MS', label: 'Mato Grosso do Sul' },
                                { value: 'MG', label: 'Minas Gerais' },
                                { value: 'PA', label: 'Pará' },
                                { value: 'PB', label: 'Paraíba' },
                                { value: 'PR', label: 'Paraná' },
                                { value: 'PE', label: 'Pernambuco' },
                                { value: 'PI', label: 'Piauí' },
                                { value: 'RJ', label: 'Rio de Janeiro' },
                                { value: 'RN', label: 'Rio Grande do Norte' },
                                { value: 'RS', label: 'Rio Grande do Sul' },
                                { value: 'RO', label: 'Rondônia' },
                                { value: 'RR', label: 'Roraima' },
                                { value: 'SC', label: 'Santa Catarina' },
                                { value: 'SP', label: 'São Paulo' },
                                { value: 'SE', label: 'Sergipe' },
                                { value: 'TO', label: 'Tocantins' },
                              ]}
                              disabled={true}
                            />
                          )}
                        />
                      </div>
                    </>
                  );
                }}
              />
            );
          })()}
        </div>

        {proposal?.requiresInspection === 'S' && (
          <InspectionContactSection
            ref={inspectionContactRef}
            disabled={isViewMode}
            initialContacts={
              proposal?.lobs?.[0]?.risks?.[0]?.inspectionContacts
            }
          />
        )}

        <div className='flex flex-row items-center justify-between gap-3 self-stretch px-4 sm:gap-6 sm:px-6'>
          <Button
            type='button'
            variant='ghost'
            size='md'
            className='flex h-12 min-w-0 items-center justify-start gap-2 px-0 text-alba-blue-500 hover:text-alba-blue-600 sm:h-auto'
            onClick={onBack}
          >
            <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
            Voltar
          </Button>
          {!isViewMode && (
            <Button
              variant='primary'
              size='md'
              className='flex h-12 min-w-[132px] items-center justify-center gap-2 rounded-xl px-5 py-2 sm:h-auto sm:min-w-[148px] sm:px-6'
              onClick={handleSubmit}
              disabled={isUpdatingProposal}
            >
              {isUpdatingProposal ? (
                <>
                  Processando...
                  <Spinner size='sm' colorScheme='gray' />
                </>
              ) : (
                <>
                  Próximo
                  <ArrowRight className='h-5 w-5' strokeWidth={1.9} />
                </>
              )}
            </Button>
          )}
        </div>

        <PropertyDeniedDialog
          isOpen={Boolean(quoteClosedError)}
          detail={quoteClosedError?.detail}
          errors={quoteClosedError?.messages ?? []}
        />
      </div>
    );
  },
});
